import { Timestamp } from "firebase/firestore";
import { toJsDate } from "./fechas";

export const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Acepta Timestamp, Date, string ISO, {seconds}, número ms — cualquier cosa
// que `toJsDate` pueda convertir a un Date válido.
export const isTimestampLike = (value) => !!toJsDate(value);

// Devuelve un Firestore Timestamp para cualquier forma de fecha soportada,
// o null si no se pudo interpretar.
const toTimestampSafe = (value) => {
  const d = toJsDate(value);
  return d ? Timestamp.fromDate(d) : null;
};

export const startOfDay = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const endOfDay = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

export const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const formatMoney = (amount) =>
  `$${toNumber(amount).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const formatDate = (ts) => {
  const d = toJsDate(ts);
  if (!d) return "—";
  return d.toLocaleDateString("es-MX");
};

export const getNombreCompleto = (persona = {}) =>
  [persona?.nombre, persona?.apellidos].filter(Boolean).join(" ").trim() || "Sin nombre";

/**
 * Devuelve la PRÓXIMA fecha FUTURA usando un día del mes como ancla.
 * Si el día N del mes actual ya pasó, salta al siguiente mes.
 * Crítico para agenda previsora: aunque el último pago fue hace meses,
 * proyectamos al día equivalente del próximo mes futuro.
 */
function proximaFechaFutura(diaAncla) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const tryDate = (anio, mes, dia) => {
    const ultimoDiaMes = new Date(anio, mes + 1, 0).getDate();
    const d = new Date(anio, mes, Math.min(dia, ultimoDiaMes));
    d.setHours(0, 0, 0, 0);
    return d;
  };
  let proxima = tryDate(hoy.getFullYear(), hoy.getMonth(), diaAncla);
  if (proxima.getTime() < hoy.getTime()) {
    proxima = tryDate(hoy.getFullYear(), hoy.getMonth() + 1, diaAncla);
  }
  return proxima;
}

export const getDueTimestamp = (data = {}) => {
  // 1. Campo explícito nextDueDate (lo escribe processPayment del front
  //    nuevo). toJsDate ahora tolera ISO/dd-mm-yyyy/objetos anidados.
  const directo = toTimestampSafe(data?.nextDueDate);
  if (directo) return directo;

  // 2. lastPaymentDate explícito → proyectar al día equivalente del futuro.
  const ultimaLP = toJsDate(data?.lastPaymentDate);
  if (ultimaLP) {
    return Timestamp.fromDate(proximaFechaFutura(ultimaLP.getDate()));
  }

  // 3. FALLBACK: deudas importadas/legacy sin nextDueDate ni
  //    lastPaymentDate. Buscamos el último pago positivo en pagos[].
  const pagos = Array.isArray(data?.pagos) ? data.pagos : [];
  let ultimoMs = 0;
  for (const p of pagos) {
    const c = Number(p?.cantidad || 0);
    if (c <= 0) continue;
    const t = toJsDate(p?.fecha);
    if (t && t.getTime() > ultimoMs) ultimoMs = t.getTime();
  }
  if (ultimoMs > 0) {
    const ult = new Date(ultimoMs);
    return Timestamp.fromDate(proximaFechaFutura(ult.getDate()));
  }

  // 4. Sin historial de pagos: fecha inicio del crédito.
  const inicio = toJsDate(
    data?.fechaInicio ||
      data?.fechaInicioDeuda ||
      data?.fechaContrato ||
      data?.fechaVenta,
  );
  if (inicio) {
    return Timestamp.fromDate(proximaFechaFutura(inicio.getDate()));
  }

  return null;
};

export const isDevolucionLike = (data = {}) => {
  const estado = String(data?.estado || "").toLowerCase();
  const estatus = String(data?.estatus || "").toLowerCase();

  return (
    data?.devolucion === true ||
    data?.esDevolucion === true ||
    estado === "en_devolucion" ||
    estado === "devuelta" ||
    estado === "cerrada_devolucion" ||
    estatus === "cancelada" ||
    estatus === "cancelado"
  );
};

export const calcularSaldoNormal = (data = {}) => {
  const montoTotal = toNumber(data?.monto ?? data?.importe);
  const enganche = toNumber(data?.enganche);
  const pagos = Array.isArray(data?.pagos) ? data.pagos : [];

  let totalPagado = 0;
  let engancheEnPagos = false;

  for (const p of pagos) {
    const cantidad = toNumber(p?.cantidad);
    const tipo = String(p?.tipo || "").toLowerCase();

    if (cantidad > 0) {
      totalPagado += cantidad;
    }

    if (tipo === "enganche" && cantidad > 0) {
      engancheEnPagos = true;
    }
  }

  if (!engancheEnPagos) {
    totalPagado += enganche;
  }

  return Math.max(montoTotal - totalPagado, 0);
};

export const calcularSaldoMostrable = (data = {}) => {
  if (isDevolucionLike(data)) {
    return Math.max(toNumber(data?.saldoRestante), 0);
  }

  const recalculado = calcularSaldoNormal(data);
  if (Number.isFinite(recalculado)) return recalculado;

  return Math.max(toNumber(data?.saldoRestante), 0);
};

export const getMensualidadMostrable = (data = {}) => {
  const mensualidad = toNumber(data?.mensualidad);
  if (mensualidad > 0) return mensualidad;

  const saldo = calcularSaldoMostrable(data);
  return saldo > 0 ? saldo : 0;
};

export const getFraccionamientoIdFromDeuda = (data = {}) => {
  if (data?.fraccionamientoId) return data.fraccionamientoId;
  if (data?.propiedades?.[0]?.fraccionamientoId) return data.propiedades[0].fraccionamientoId;
  return null;
};

export const getFraccionamientoNombreFallback = (data = {}) => {
  return (
    data?.fraccionamientoNombre ||
    data?.fraccNombre ||
    data?.propiedades?.[0]?.fraccionamientoNombre ||
    data?.propiedades?.[0]?.fraccNombre ||
    "—"
  );
};

export const getDiasHastaVencimiento = (dueTs, baseDate = new Date()) => {
  const due = toJsDate(dueTs);
  if (!due) return null;
  const ms = startOfDay(due) - startOfDay(baseDate);
  return Math.ceil(ms / 86400000);
};

export const getDiasMora = (dueTs, baseDate = new Date()) => {
  const due = toJsDate(dueTs);
  if (!due) return 0;
  const diffMs = startOfDay(baseDate).getTime() - startOfDay(due).getTime();
  return diffMs > 0 ? Math.floor(diffMs / 86400000) : 0;
};

export const getRiesgoMora = (diasMora = 0) => {
  if (diasMora >= 90) return "alto";
  if (diasMora >= 30) return "medio";
  return "bajo";
};

export const getRangoPrevision = (dias = null) => {
  if (dias == null) return 30;
  if (dias <= 7) return 7;
  if (dias <= 15) return 15;
  return 30;
};

export const getTextoBusquedaAgenda = (item = {}) =>
  [
    item?.cliente,
    item?.responsableNombre,
    item?.telefono,
    item?.direccion,
    item?.referencia,
    item?.fraccionamiento,
    item?.estado,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

    export const getMesesAtrasoReales = (dueTs, baseDate = new Date()) => {
  const dueRaw = toJsDate(dueTs);
  if (!dueRaw) return 0;

  const due = startOfDay(dueRaw);
  const base = startOfDay(baseDate);

  if (due > base) return 0;

  let months =
    (base.getFullYear() - due.getFullYear()) * 12 +
    (base.getMonth() - due.getMonth());

  // Si ya pasó el día de corte de este mes, cuenta otra mensualidad vencida
  if (base.getDate() >= due.getDate()) {
    months += 1;
  }

  return Math.max(months, 1);
};

export const getSaldoAtrasado = (data = {}) => {
  const mensualidad = toNumber(data?.mensualidad);
  const fechaInicio = getFechaInicioReal(data);
  const pagos = Array.isArray(data?.pagos) ? data.pagos : [];
  const enganche = toNumber(data?.enganche);

  if (!mensualidad || mensualidad <= 0) return 0;

  // 🧮 meses transcurridos reales
  const hoy = new Date();
  const mesesTranscurridos =
    (hoy.getFullYear() - fechaInicio.getFullYear()) * 12 +
    (hoy.getMonth() - fechaInicio.getMonth());

  // 🔥 esperado acumulado
  const esperado = mesesTranscurridos * mensualidad + enganche;

  // 💰 pagado real
  let pagado = 0;
  for (const p of pagos) {
    const cantidad = toNumber(p?.cantidad);
    if (cantidad > 0) pagado += cantidad;
  }

  // 🔥 atraso real
  return Math.max(esperado - pagado, 0);
};

export const getCuotasProximas = (data = {}, baseDate = new Date()) => {
  const nextDueDate = getDueTimestamp(data);
  const dueRaw = toJsDate(nextDueDate);
  if (!dueRaw) return 0;

  const due = startOfDay(dueRaw);
  const base = startOfDay(baseDate);

  if (due < base) return 0;

  // Con tu modelo actual, nextDueDate representa la próxima mensualidad a cobrar
  return 1;
};

export const getSaldoProximo = (data = {}, baseDate = new Date()) => {
  const saldoRestante = calcularSaldoMostrable(data);
  const mensualidad = getMensualidadMostrable(data);
  const cuotasProximas = getCuotasProximas(data, baseDate);

  if (cuotasProximas <= 0 || mensualidad <= 0 || saldoRestante <= 0) return 0;

  return Math.min(mensualidad * cuotasProximas, saldoRestante);
};

export const getRiesgoAtrasoPorMeses = (mesesAtraso = 0) => {
  if (mesesAtraso >= 3) return "alto";
  if (mesesAtraso >= 2) return "medio";
  if (mesesAtraso >= 1) return "bajo";
  return "al_corriente";
};
export const getCarteraCategoria = (mesesAtraso = 0) => {
  if (mesesAtraso >= 10) return "D";
  if (mesesAtraso >= 7) return "C";
  if (mesesAtraso >= 4) return "B";
  if (mesesAtraso >= 1) return "A";
  return "AL_CORRIENTE";
};

export const getCarteraDescripcion = (categoria = "") => {
  switch (categoria) {
    case "A":
      return "1 a 3 meses";
    case "B":
      return "4 a 6 meses";
    case "C":
      return "7 a 9 meses";
    case "D":
      return "10+ meses";
    default:
      return "Sin atraso";
  }
};

export const getFechaInicioReal = (data = {}) => {
  const pagos = Array.isArray(data?.pagos) ? data.pagos : [];

  let fechas = [];

  // incluir fechas de pagos
  for (const p of pagos) {
    const d = toJsDate(p?.fecha);
    if (d) fechas.push(d);
  }

  // incluir fecha de enganche si existe como campo separado
  const dEng = toJsDate(data?.fechaEnganche);
  if (dEng) fechas.push(dEng);

  // fallback (si no hay nada)
  if (fechas.length === 0) {
    return toJsDate(data?.fechaCreacion) || new Date();
  }

  // 🔥 tomar la más antigua
  return new Date(Math.min(...fechas.map(d => d.getTime())));
};